import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { roadmap, phaseColorMap, agentMap } from "@/lib/mockData";

const TOTAL_DAYS = 90;
const dayPct = (d) => ((d - 1) / TOTAL_DAYS) * 100;
const widthPct = (days) => (days / TOTAL_DAYS) * 100;

const SHOWCASE_TASK_IDS = ["rt1", "rt7", "rt8", "rt10", "rt15"];

export default function RoadmapTeaser() {
  const showcase = roadmap.tasks.filter((t) => SHOWCASE_TASK_IDS.includes(t.id));
  const todayLeft = `${dayPct(roadmap.currentDay)}%`;

  return (
    <section
      data-testid="roadmap-teaser"
      id="roadmap"
      className="px-5 py-24 md:px-8 md:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end"
        >
          <div>
            <div className="eyebrow">Plan</div>
            <h2 className="mt-3 text-[40px] font-black leading-[1.02] tracking-tight text-zinc-900 md:text-[60px]">
              A 90-day plan<br />that updates itself.
            </h2>
          </div>
          <p className="max-w-md text-[14px] leading-relaxed text-zinc-600">
            Idea AI generates and maintains a phased roadmap. Tasks reschedule
            when blockers move. Milestones pulse when at risk.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.5 }}
          className="card-soft overflow-hidden rounded-[28px] p-6"
        >
          <div className="relative">
            {/* Phase bands */}
            <div className="relative h-7" data-testid="teaser-phase-bands">
              {roadmap.phases.map((p, idx) => {
                const c = phaseColorMap[p.color];
                return (
                  <div
                    key={p.id}
                    data-testid={`teaser-phase-band-${p.id}`}
                    className="absolute top-1 flex items-center overflow-hidden rounded-md px-2.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                    style={{
                      left: `${dayPct(p.startDay)}%`,
                      width: `calc(${widthPct(p.endDay - p.startDay + 1)}% - 2px)`,
                      height: 22,
                      backgroundColor: c.band,
                      color: c.solid,
                    }}
                  >
                    Phase {idx + 1} · {p.name}
                  </div>
                );
              })}
            </div>

            {/* Axis */}
            <div className="relative mt-3 h-7 border-t border-black/[0.08]">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((wn) => (
                <div
                  key={wn}
                  className="absolute top-2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500"
                  style={{ left: `${dayPct((wn - 1) * 7 + 4)}%` }}
                >
                  W{wn}
                </div>
              ))}
            </div>

            {/* Tasks (5 showcase) */}
            <div
              data-testid="teaser-tasks"
              className="relative mt-6"
              style={{ height: 56 + 28 }}
            >
              {showcase.map((t, i) => {
                const isHuman = t.owner === "HUMAN";
                const a = agentMap[t.owner];
                const phase = roadmap.phases.find((p) => p.id === t.phaseId);
                const c = phaseColorMap[phase.color];
                return (
                  <div
                    key={t.id}
                    data-testid={`teaser-task-${t.id}`}
                    className="absolute flex h-9 items-center gap-2 overflow-hidden rounded-lg border border-black/[0.05] bg-white pl-3 pr-2.5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                    style={{
                      left: `${dayPct(t.startDay)}%`,
                      width: `calc(${widthPct(t.durationDays)}% - 4px)`,
                      top: i * 18,
                      backgroundColor: i % 2 === 0 ? "#ffffff" : c.band,
                    }}
                  >
                    <span
                      className="absolute left-0 top-0 h-full w-[3px]"
                      style={{
                        backgroundColor: isHuman ? "#F97316" : c.solid,
                      }}
                    />
                    <span className="truncate text-[10.5px] font-semibold text-zinc-900">
                      {t.title}
                    </span>
                    {isHuman ? (
                      <span className="ml-auto rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-orange-700">
                        Human
                      </span>
                    ) : (
                      <span className="ml-auto inline-flex h-4 w-4 items-center justify-center rounded-full bg-zinc-100 text-[9px] font-extrabold text-zinc-700">
                        {a?.name?.[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Today line */}
            <div
              data-testid="teaser-today-line"
              className="pointer-events-none absolute"
              style={{ left: todayLeft, top: 0, bottom: 0, width: 0 }}
            >
              <div className="absolute -left-px top-7 h-[calc(100%-1.75rem)] w-[2px] bg-zinc-900/80" />
              <div
                className="absolute -translate-x-1/2 rounded-full bg-zinc-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white"
                style={{ top: 0 }}
              >
                Today · Day {roadmap.currentDay}
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-6">
          <Link
            to="/roadmap"
            data-testid="roadmap-teaser-cta"
            className="group inline-flex items-center gap-1.5 text-[14px] font-semibold text-zinc-900 hover:text-black"
          >
            See the roadmap
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
